import { Component, NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { SidenavMenuComponent } from './layouts/sidenav-menu/sidenav-menu.component';
import { AuthGuardV2Service } from './v2/services/auth-guard/AuthGuardV2Service';
import { PublicPageGuard } from './v2/services/public-page-guard/public-page.guard';
import { FeatureFlagGuard } from './v2/services/feature-flag-guard/feature-flag.guard';
import { AccountSettingGuard } from './v2/services/account-setting-guard/account-setting.guard';
import { CustomPreloadingStrategyService } from '@service/customPreloadingStrategy/custom-preloading-strategy.service';
import { environment } from '@environment/environment';
import { ACCOUNT_TYPE } from '@domain/constants/app-variables';

const routes: Routes = [
	{ path: '', redirectTo: '/login', pathMatch: 'full' },
	{
		path: 'login',
		loadChildren: () => import('./v2/components/login/login.module').then((m) => m.LoginModule),
		data: { type: ['ALL'], role: ['ALL'] },
	},
	{
		path: 'capacity',
		component: SidenavMenuComponent,
		loadChildren: () =>
			import('./capacity-search/capacity-search.module').then((m) => m.CapacitySearchModule),
		canActivate: [AuthGuardV2Service],
		data: { type: [ACCOUNT_TYPE.BROKER, ACCOUNT_TYPE.SHIPPER, ACCOUNT_TYPE.ADMIN], role: ['ALL'] },
	},
	// CN-8465: Scorecard redirects must appear before the generic 'users'
	// route, otherwise Angular matches 'users' first and the redirects
	// are never evaluated.
	{ path: 'users/scorecards/lanes', redirectTo: 'reports/scorecards/lanes', pathMatch: 'full' },
	{ path: 'users/scorecards/carriers', redirectTo: 'reports/scorecards/carriers', pathMatch: 'full' },
	{ path: 'users/scorecards/customers', redirectTo: 'reports/scorecards/customers', pathMatch: 'full' },
	{ path: 'users/scorecards/BAPerformanceMetrics', redirectTo: 'reports/scorecards/BAPerformanceMetrics', pathMatch: 'full' },
	{ path: 'users/scorecards', redirectTo: 'reports/scorecards/lanes', pathMatch: 'full' },
	{
		path: 'users',
		component: SidenavMenuComponent,
		loadChildren: () => import('./broker/broker.module').then((m) => m.BrokerModule),
		canActivate: [AuthGuardV2Service],
		data: {
			type: [ACCOUNT_TYPE.BROKER, ACCOUNT_TYPE.SHIPPER, ACCOUNT_TYPE.CARRIER, ACCOUNT_TYPE.ADMIN],
			role: ['ALL'],
		},
	},

	{
		path: 'projects',
		component: SidenavMenuComponent,
		loadChildren: () => import('./project/project.module').then((m) => m.ProjectModule),
		canActivate: [AuthGuardV2Service],
		data: { type: [ACCOUNT_TYPE.BROKER, ACCOUNT_TYPE.SHIPPER], role: ['ALL'] },
	},
	{
		path: 'v2/projects',
		component: SidenavMenuComponent,
		loadChildren: () => import('./v2/project/project.module').then((m) => m.ProjectModule),
		canActivate: [AuthGuardV2Service],
		data: { type: [ACCOUNT_TYPE.BROKER, ACCOUNT_TYPE.SHIPPER], role: ['ALL'] },
	},
	// Add new routes when feature flag is enabled
	...(environment.FEATURE_FLAGS.SPLIT_PROJECTS_MENU_ENABLED ? [
		{
			path: 'v2/procurement',
			component: SidenavMenuComponent,
			loadChildren: () => import('./v2/procurement/procurement.module').then((m) => m.ProcurementModule),
			canActivate: [AuthGuardV2Service],
			data: { type: [ACCOUNT_TYPE.BROKER, ACCOUNT_TYPE.SHIPPER], role: ['ALL'] },
		},
		{
			path: 'v2/workflows',
			component: SidenavMenuComponent,
			loadChildren: () => import('./v2/workflows/workflows.module').then((m) => m.WorkflowsModule),
			canActivate: [AuthGuardV2Service],
			data: { type: [ACCOUNT_TYPE.BROKER, ACCOUNT_TYPE.SHIPPER], role: ['ALL'] },
		}
	] : []),
	{
		path: 'carriers',
		component: SidenavMenuComponent,
		loadChildren: () => import('./carriers/carriers.module').then((m) => m.CarriersModule),
		canActivate: [AuthGuardV2Service],
		data: { type: ['ALL'], role: ['ALL'] },
	},
	{
		path: 'v2/carriers',
		component: SidenavMenuComponent,
		loadChildren: () =>
			import('./v2/carriers-network/carriers-network.module').then((m) => m.CarriersNetworkModule),
		canActivate: [AuthGuardV2Service],
		data: { type: [ACCOUNT_TYPE.BROKER, ACCOUNT_TYPE.SHIPPER], role: ['ALL'] },
	},
	{
		path: 'v2/loads/:quoteId',
		component: SidenavMenuComponent,
		loadChildren: () =>
			import('./v2/components/quote-details/quote-details-v2.module').then(
				(m) => m.QuoteDetailsV2Module
			),
		canActivate: [AuthGuardV2Service],
		data: { type: [ACCOUNT_TYPE.BROKER, ACCOUNT_TYPE.SHIPPER], role: ['ALL'] },
	},
	{
		path: 'admin',
		component: SidenavMenuComponent,
		loadChildren: () => import('./admin/admin.module').then((m) => m.AdminModule),
		canActivate: [AuthGuardV2Service],
		data: { type: [ACCOUNT_TYPE.ADMIN], role: ['ALL'] },
	},
	{
		path: 'bid',
		// data: { preload: true, loadAfterSeconds: 2 },
		loadChildren: () => import('./bid/bid.module').then((m) => m.BidModule),
	},
	{
		path: 'loadsSourcing',
		data: { preload: true, loadAfterSeconds: 2 },
		canActivate: [PublicPageGuard],
		loadChildren: () =>
			import('./loads-matching/loads-matching.module').then((m) => m.LoadsMatchingModule),
	},
	// enable this route if rate confirmation feature is enabled
	// {
	// 	path: 'signRateConfirmation/:bookingId',
	// 	loadChildren: () =>
	// 		import('./sign-rate-confirmation/sign-rate-confirmation.module').then(
	// 			(m) => m.SignRateConfirmationModule
	// 		),
	// },
	{
		path: 'plans',
		loadChildren: () => import('./plans/plans.module').then((m) => m.PlansModule),
		data: { type: ['ALL'], role: ['ALL'] },
	},
	{
		path: 'register',
		loadChildren: () => import('./register/register.module').then((m) => m.RegisterModule),
		data: { type: ['ALL'], role: ['ALL'] },
	},
	{
		path: 'register/carrier/success',
		loadChildren: () =>
			import('./carrier-registration-successful/carrier-registration-successful.module').then(
				(m) => m.CarrierRegistrationSuccessfulModule
			),
	},
	{
		path: 'user_survey',
		loadChildren: () => import('./user-survey/user-survey.module').then((m) => m.UserSurveyModule),
		data: { type: ['BROKER', 'SHIPPER'], role: ['ALL'] },
		canActivate: [AuthGuardV2Service],
	},
	{
		path: 'reset_password/:hashKey',
		loadChildren: () =>
			import('./reset-password/reset-password.module').then((m) => m.ResetPasswordModule),
	},
	{
		path: 'expired_link',
		loadChildren: () =>
			import('./expired-link/expired-link.module').then((m) => m.ExpiredLinkModule),
	},
	{
		path: 'forgot_password',
		loadChildren: () =>
			import('./reset-password/reset-password.module').then((m) => m.ResetPasswordModule),
	},
	{
		path: 'maintenance',
		loadChildren: () => import('./maintenance/maintenance.module').then((m) => m.MaintenanceModule),
	},
	{
		path: 'email_verified/:userRandomString/:setPassword',
		loadChildren: () =>
			import('./email-verified/email-verified.module').then((m) => m.EmailVerifiedModule),
	},
	{
		path: 'change_browser',
		loadChildren: () => import('./edge-ie-msg/edge-ie-msg.module').then((m) => m.EdgeIeMsgModule),
	},
	{
		path: 'project_rate_request',
		loadChildren: () =>
			import('./project-rate-request/project-rate-request.module').then(
				(m) => m.ProjectRateRequestModule
			),
	},
	{
		path: 'carrier',
		data: { type: [ACCOUNT_TYPE.CARRIER] },
		component: SidenavMenuComponent,
		loadChildren: () =>
			import('./carriers-view/carriers-view.module').then((m) => m.CarriersViewModule),
		canActivate: [AuthGuardV2Service],
	},
	{
		path: 'pricing',
		component: SidenavMenuComponent,
		loadChildren: () => import('./pricing/pricing.module').then((m) => m.PricingModule),
		canActivate: [AuthGuardV2Service],
		data: { type: ['ALL'], role: ['ALL'] },
	},
	{
		path: 'dashboard',
		component: SidenavMenuComponent,
		loadChildren: () => import('./dashboard/dashboard.module').then((m) => m.DashboardModule),
		canActivate: [AuthGuardV2Service],
		data: { type: [ACCOUNT_TYPE.BROKER, ACCOUNT_TYPE.SHIPPER], role: ['ALL'] },
	},
	{
		component: SidenavMenuComponent,
		path: 'background-jobs',
		loadChildren: () =>
			import('./v2/components/background-jobs/background-jobs.module').then(
				(m) => m.BackgroundJobsModule
			),
		canActivate: [AuthGuardV2Service],
		data: { type: [ACCOUNT_TYPE.BROKER, ACCOUNT_TYPE.SHIPPER], role: ['ALL'] },
	},
	{
		path: 'booking-assistant-automation',
		component: SidenavMenuComponent,
		loadChildren: () =>
			import(
				'./broker/automation/booking-assistant-automation/booking-assistant-automation.module'
			).then((m) => m.BookingAssistantAutomationModule),
		canActivate: [AuthGuardV2Service],
		data: { type: [ACCOUNT_TYPE.BROKER, ACCOUNT_TYPE.SHIPPER], role: ['ALL'] },
	},
	{
		path: 'v2/loadsList',
		component: SidenavMenuComponent,
		loadChildren: () => import('./v2/loads/loads.module').then((m) => m.LoadsModule),
		canActivate: [AuthGuardV2Service],
		data: { type: [ACCOUNT_TYPE.BROKER, ACCOUNT_TYPE.SHIPPER], role: ['ALL'] },
	},
	{
		path: 'v2/offers',
		component: SidenavMenuComponent,
		loadChildren: () => import('./v2/offers/offers.module').then((m) => m.OffersModule),
		canActivate: [AuthGuardV2Service],
		data: { type: [ACCOUNT_TYPE.BROKER, ACCOUNT_TYPE.SHIPPER], role: ['ALL'] },
	},
	// =========================================================================
	// CN-8465: All Reports-dropdown items consolidated under /reports/
	// =========================================================================
	{
		path: 'reports/scorecards',
		component: SidenavMenuComponent,
		loadChildren: () => import('./broker/scorecards/scorecards.module').then((m) => m.ScorecardsModule),
		canActivate: [AuthGuardV2Service],
		data: { type: [ACCOUNT_TYPE.BROKER, ACCOUNT_TYPE.SHIPPER], role: ['ALL'] },
	},
	{
		path: 'reports/offer-analytics',
		component: SidenavMenuComponent,
		loadChildren: () => import('./v2/offer-analytics/offer-analytics.module').then((m) => m.OfferAnalyticsModule),
		canActivate: [AuthGuardV2Service],
		data: { type: [ACCOUNT_TYPE.BROKER, ACCOUNT_TYPE.SHIPPER], role: ['ALL'] },
	},
	{
		path: 'reports/lane-analytics',
		component: SidenavMenuComponent,
		loadChildren: () => import('./v2/lane-analytics/lane-analytics.module').then((m) => m.LaneAnalyticsModule),
		canActivate: [AuthGuardV2Service, FeatureFlagGuard],
		data: {
			type: [ACCOUNT_TYPE.BROKER, ACCOUNT_TYPE.SHIPPER],
			role: ['ALL'],
			featureFlag: 'LANE_ANALYSIS_DASHBOARD_ENABLED'
		},
	},
	{
		path: 'reports/lane-carrier-analytics',
		component: SidenavMenuComponent,
		loadChildren: () => import('./v2/lane-carrier-stats/lane-carrier-stats.module').then((m) => m.LaneCarrierStatsModule),
		canActivate: [AuthGuardV2Service],
		data: { type: [ACCOUNT_TYPE.BROKER, ACCOUNT_TYPE.SHIPPER], role: ['ALL'] },
	},
	{
		// CN-9074 (Coverage / plan B6). Gated on the STRONG_LANES entitlement, not a new
		// account setting: Coverage ABSORBS Strong Lanes' Top Lanes list and re-skins its lane
		// detail as the drill-down (plan decision 5), so the accounts entitled to Strong Lanes
		// are exactly the pilot set Coverage is for. c4's CoverageController enforces the same
		// gate server-side -- these guards decide what is reachable, not what is readable.
		path: 'reports/coverage',
		component: SidenavMenuComponent,
		loadChildren: () => import('./reports/coverage/coverage.module').then((m) => m.CoverageModule),
		canActivate: [AuthGuardV2Service, FeatureFlagGuard, AccountSettingGuard],
		data: {
			type: [ACCOUNT_TYPE.BROKER],
			role: ['ALL'],
			featureFlag: 'COVERAGE_ENABLED',
			accountSetting: 'STRONG_LANES'
		},
	},
	{
		path: 'reports/strong-lanes',
		component: SidenavMenuComponent,
		loadChildren: () => import('./v2/strong-lanes/strong-lanes.module').then((m) => m.StrongLanesModule),
		canActivate: [AuthGuardV2Service, FeatureFlagGuard, AccountSettingGuard],
		data: {
			type: [ACCOUNT_TYPE.BROKER],
			role: ['ALL'],
			featureFlag: 'STRONG_LANES_ENABLED',
			accountSetting: 'STRONG_LANES'
		},
	},
	{
		path: 'reports/workflow-performance',
		component: SidenavMenuComponent,
		loadChildren: () =>
			import('./reports/workflow-performance/workflow-performance.module').then(
				(m) => m.WorkflowPerformanceModule
			),
		canActivate: [AuthGuardV2Service, AccountSettingGuard],
		data: {
			type: [ACCOUNT_TYPE.BROKER, ACCOUNT_TYPE.SHIPPER],
			role: ['ALL'],
			accountSetting: 'WORKFLOW_PERFORMANCE_REPORT'
		},
	},
	{
		// CN-8580 — Reports > Recommended Carriers (MANAGER-only).
		// Component-level guard inside RecommendedCarriersComponent enforces
		// the MANAGER role; the AccountSettingGuard here covers the account-level
		// TOP_CARRIERS_ENABLED toggle.
		path: 'reports/recommended-carriers',
		component: SidenavMenuComponent,
		loadChildren: () =>
			import('./reports/recommended-carriers/recommended-carriers.module').then(
				(m) => m.RecommendedCarriersModule
			),
		canActivate: [AuthGuardV2Service, AccountSettingGuard],
		data: {
			type: [ACCOUNT_TYPE.BROKER, ACCOUNT_TYPE.SHIPPER],
			role: ['ALL'],
			accountSetting: 'TOP_CARRIERS_ENABLED'
		},
	},
	{
		// CN-7634 — customer-facing Usage report. AuthGuardV2Service covers
		// broker/shipper account type; the component + nav menu enforce the
		// ADMIN/MANAGER role (AuthGuardV2Service does not gate on `role`), and the
		// c4 endpoint scopes data to the session account.
		path: 'reports/usage',
		component: SidenavMenuComponent,
		loadChildren: () => import('./reports/usage/usage.module').then((m) => m.UsageModule),
		canActivate: [AuthGuardV2Service],
		data: {
			type: [ACCOUNT_TYPE.BROKER, ACCOUNT_TYPE.SHIPPER],
			role: ['ADMIN', 'MANAGER'],
		},
	},
	// Redirects: preserve bookmarks / shared links from old paths
	{ path: 'v2/offer-analytics', redirectTo: 'reports/offer-analytics', pathMatch: 'full' },
	{ path: 'v2/lane-analytics', redirectTo: 'reports/lane-analytics', pathMatch: 'full' },
	{ path: 'v2/lane-carrier-stats', redirectTo: 'reports/lane-carrier-analytics', pathMatch: 'full' },
	{ path: 'users/scorecards/lanes', redirectTo: 'reports/scorecards/lanes', pathMatch: 'full' },
	{ path: 'users/scorecards/carriers', redirectTo: 'reports/scorecards/carriers', pathMatch: 'full' },
	{ path: 'users/scorecards/customers', redirectTo: 'reports/scorecards/customers', pathMatch: 'full' },
	{ path: 'users/scorecards/BAPerformanceMetrics', redirectTo: 'reports/scorecards/BAPerformanceMetrics', pathMatch: 'full' },
	{ path: 'users/scorecards', redirectTo: 'reports/scorecards/lanes', pathMatch: 'full' },
	{
		path: 'email_approved/:hashKey',
		canActivate: [PublicPageGuard],
		loadChildren: () =>
			import('./email-approved/email-approved.module').then((m) => m.EmailApprovedModule),
	},
	{
		path: 'carrier_registration_request_confirmed/:hashKey',
		canActivate: [PublicPageGuard],
		loadChildren: () =>
			import(
				'./carrier-registration-request-confirmed/carrier-registration-request-confirmed.module'
			).then((m) => m.CarrierRegistrationRequestConfirmedModule),
	},
	// Legacy v2 redirects — preserve bookmarks / shared links
	{ path: 'v2/offer-analytics', redirectTo: 'reports/offer-analytics', pathMatch: 'full' },
	{ path: 'v2/lane-analytics', redirectTo: 'reports/lane-analytics', pathMatch: 'full' },
	{ path: 'v2/lane-carrier-stats', redirectTo: 'reports/lane-carrier-analytics', pathMatch: 'full' },
	{ path: '**', redirectTo: 'capacity', pathMatch: 'full' },
];

@NgModule({
	imports: [
		RouterModule.forRoot(routes, {
			useHash: true,
			preloadingStrategy: CustomPreloadingStrategyService,
		}),
	],
	exports: [RouterModule],
})
export class AppRoutingModule {}
